import {
    Mutation,
    MutationAction,
    Action,
    VuexModule,
    getModule,
    Module,
} from 'vuex-module-decorators';
import { SysConst as SC } from '@/trials/plugins/const';
import Api from '@/trials/lib/api';
import store from '@/trials/store/index';

import ConfirmDialogData from '@/trials/domain/objects/confirmDialogData';
import NotificationDialogData from '@/trials/domain/objects/notificationDialogData';
import ErrorDialogData from '@/trials/exceptions/errorDialogData';
import TrainParams from '@/trials/domain/objects/trainParams';
import KnowledgeChunks from '@/trials/domain/objects/knowledgeChunks';
import TrainData from '@/trials/domain/objects/trainData';
import TrainColumn from '@/trials/domain/objects/trainColumn';
import PredictData from '@/trials/domain/objects/predictData';
import PredictColumn from '@/trials/domain/objects/predictColumn';
import PredictResults from '@/trials/domain/objects/predictResults';

import TrainDataStatistics from '@/trials/domain/models/trainDataStatistics';
import PredictDataStatistics from '@/trials/domain/models/predictDataStatistics';

export interface ISessionState {
    // 一意のID
    uuid: string;

    // プライバシーポリシー ( true: 同意済 / false: 同意していない )
    agreed: boolean;

    // API呼び出し用token
    authToken: string;

    // KC列挙（ 0: 未実施 / 1: 実施中 / 2: 実施済 ）
    listed: number;

    // KC一覧
    kclistData: KnowledgeChunks | null;

    // 学習（ 0: 未実施 / 1: 実施中 / 2: 実施済 ）
    trained: number;

    // 学習データのアップロード状態（ 0: 未実施 / 1: 実施中 / 2: 実施済 ）
    trainingDataUploaded: number;

    // 学習データ
    trainingData: TrainData | null;

    // 入力された学習データのヘッダ
    inputTrainingDataHeaders: any[];

    // 前処理済みの学習データ
    preprocessedTrainingData: any[];

    // 学習データに対するナレッジチャンク
    trainKnowledgeChunks: KnowledgeChunks | null;

    // 学習データ中の1件に対するIndex(バイナリ化前)
    trainPersonalIndexBefore: number;

    // 学習データ中の1件に対するIndex(バイナリ化後)
    trainPersonalIndexAfter: number;

    // 学習データ中の1件に対するナレッジチャンク(バイナリ化前)
    trainPersonalChunksBefore: KnowledgeChunks | null;

    // 学習データ中の1件に対するナレッジチャンク(バイナリ化後)
    trainPersonalChunksAfter: KnowledgeChunks | null;

    // 学習データの統計情報
    trainDataStatistics: TrainColumn[];

    // 学習結果のパラメータ、スコア
    trainParams: TrainParams | null;

    // 予測フェーズは有効か(true: 有効 / false: 無効)
    enabledPredictPhase: boolean;

    // 予測（ 0: 未実施 / 1: 実施中 / 2: 実施済 ）
    predicted: number;

    // 予測データのアップロード状態（ 0: 未実施 / 1: 実施中 / 2: 実施済 ）
    predictDataUploaded: number;

    // 予測データ
    predictData: PredictData | null;

    // 入力された予測データのヘッダ
    inputPredictDataHeaders: any[];

    // 前処理済み予測データ
    preprocessedPredictData: PredictData | null;

    // 学習データ中の1件に対するIndex(バイナリ化前)
    predictPersonalIndexBefore: number;

    // 学習データ中の1件に対するIndex(バイナリ化後)
    predictPersonalIndexAfter: number;

    // 予測データ中の1件に対するナレッジチャンク(バイナリ化前)
    predictPersonalChunksBefore: KnowledgeChunks | null;

    // 予測データ中の1件に対するナレッジチャンク(バイナリ化後)
    predictPersonalChunksAfter: KnowledgeChunks | null;

    // 予測データの統計情報
    predictDataStatistics: PredictColumn[];

    // 予測結果
    predictResults: PredictResults | null;

    // 試行画面で表示中のコンポーネント
    currentRouterViewTrials: string;

    // KC列挙画面で表示中のコンポーネント
    currentRouterViewKCList: string;

    // 学習データ画面で表示中のコンポーネント
    currentRouterViewTrainData: string;

    // 学習結果画面で表示中のコンポーネント
    currentRouterViewTrainResult: string;

    // 学習結果 全体で表示中のコンポーネント
    currentRouterViewTrainResultAll: string;

    // 予測データ画面で表示中のコンポーネント
    currentRouterViewPredictData: string;

    // 予測結果画面で表示中のコンポーネント
    currentRouterViewPredictResult: string;

    // 確認ダイアログデータ
    confirmDialogData: ConfirmDialogData | null;

    // 通知ダイアログデータ
    notificationDialogData: NotificationDialogData | null;

    // エラーダイアログデータ
    errorDialogData: ErrorDialogData | null;
}
@Module({ dynamic: true, store, name: 'session', namespaced: true })
class Session extends VuexModule implements ISessionState {
    /*----------------------------------*
     * 初期値設定
     *-----------------------------------*/
    // state
    uuid = '';
    agreed = false;
    authToken = '';

    // KC列挙
    listed = 0;
    kclistData: KnowledgeChunks | null = null;

    // 学習
    trained = 0;
    trainingDataUploaded = 0;
    trainingData: TrainData | null = null;
    inputTrainingDataHeaders: any[] = [];
    preprocessedTrainingData: any[] = [];
    trainKnowledgeChunks: KnowledgeChunks | null = null;
    trainPersonalIndexBefore = 0;
    trainPersonalIndexAfter = 0;
    trainPersonalChunksBefore: KnowledgeChunks | null = null;
    trainPersonalChunksAfter: KnowledgeChunks | null = null;
    trainDataStatistics: any[] = [];
    trainParams: TrainParams | null = null;

    // 予測
    enabledPredictPhase = false;
    predicted = 0;
    predictDataUploaded = 0;
    predictData: PredictData | null = null;
    inputPredictDataHeaders: any[] = [];
    preprocessedPredictData: PredictData | null = null;
    predictPersonalIndexBefore = 0;
    predictPersonalIndexAfter = 0;
    predictPersonalChunksBefore: KnowledgeChunks | null = null;
    predictPersonalChunksAfter: KnowledgeChunks | null = null;
    predictDataStatistics: PredictColumn[] = [];
    predictResults: PredictResults | null = null;

    // 表示コンポーネント
    currentRouterViewTrials: string = '';
    currentRouterViewKCList: string = '';
    currentRouterViewTrainData: string = '';
    currentRouterViewTrainResult: string = '';
    currentRouterViewTrainResultAll: string = '';
    currentRouterViewPredictData: string = '';
    currentRouterViewPredictResult: string = '';

    // ダイアログデータ
    confirmDialogData: ConfirmDialogData | null = null;
    notificationDialogData: NotificationDialogData | null = null;
    errorDialogData: ErrorDialogData | null = null;

    /*----------------------------------*
     * mutations ( ≒ setters)
     *
     * ※値更新はmutationからのみ実施可能
     * ※mutationはActionからのみCall可能
     *-----------------------------------*/
    @Mutation
    public SET_UUID(newUuid: string) {
        this.uuid = newUuid;
    }
    @Mutation
    public SET_AGREED(flag: boolean) {
        this.agreed = flag;
    }
    @Mutation
    public SET_AUTHTOKEN(newToken: string) {
        this.authToken = newToken;
    }
    @Mutation
    public SET_LISTED(flag: number) {
        this.listed = flag;
    }
    @Mutation
    public SET_KCLIST_DATA(newData: KnowledgeChunks | null) {
        this.kclistData = newData;
    }
    @Mutation
    public SET_TRAINED(flag: number) {
        this.trained = flag;
    }
    @Mutation
    public SET_TRAINING_DATA_UPLOADED(flag: number) {
        this.trainingDataUploaded = flag;
    }
    @Mutation
    public SET_TRAINING_DATA(newData: TrainData | null) {
        this.trainingData = newData;
    }
    @Mutation
    public SET_INPUT_TRAINING_DATA_HEADERS(newData: any[]) {
        this.inputTrainingDataHeaders = newData;
    }
    @Mutation
    public SET_PREPROCESSED_TRAINING_DATA(newData: any[]) {
        this.preprocessedTrainingData = newData;
    }
    @Mutation
    public SET_TRAIN_CHUNKS(newData: KnowledgeChunks | null) {
        this.trainKnowledgeChunks = newData;
    }
    @Mutation
    public SET_TRAIN_PERSONAL_INDEX_BEFORE(newIdx: number) {
        this.trainPersonalIndexBefore = newIdx;
    }
    @Mutation
    public SET_TRAIN_PERSONAL_INDEX_AFTER(newIdx: number) {
        this.trainPersonalIndexAfter = newIdx;
    }
    @Mutation
    public SET_TRAIN_PERSONAL_CHUNKS_BEFORE(newData: KnowledgeChunks | null) {
        this.trainPersonalChunksBefore = newData;
    }
    @Mutation
    public SET_TRAIN_PERSONAL_CHUNKS_AFTER(newData: KnowledgeChunks | null) {
        this.trainPersonalChunksAfter = newData;
    }
    @Mutation
    public SET_TRAIN_DATA_STATISTICS(newData: TrainColumn[]) {
        this.trainDataStatistics = newData;
    }
    @Mutation
    public SET_TRAIN_PARAMS(newData: TrainParams | null) {
        this.trainParams = newData;
    }
    @Mutation
    public SET_ENABLED_PREDICT_PHASE(flag: boolean) {
        this.enabledPredictPhase = flag;
    }
    @Mutation
    public SET_PREDICTED(flag: number) {
        this.predicted = flag;
    }
    @Mutation
    public SET_PREDICT_DATA_UPLOADED(flag: number) {
        this.predictDataUploaded = flag;
    }
    @Mutation
    public SET_PREDICT_DATA(newData: PredictData | null) {
        this.predictData = newData;
    }
    @Mutation
    public SET_INPUT_PREDICT_DATA_HEADERS(newData: any[]) {
        this.inputPredictDataHeaders = newData;
    }
    @Mutation
    public SET_PREPROCESSED_PREDICT_DATA(newData: PredictData | null) {
        this.preprocessedPredictData = newData;
    }
    @Mutation
    public SET_PREDICT_PERSONAL_INDEX_BEFORE(newIdx: number) {
        this.predictPersonalIndexBefore = newIdx;
    }
    @Mutation
    public SET_PREDICT_PERSONAL_INDEX_AFTER(newIdx: number) {
        this.predictPersonalIndexAfter = newIdx;
    }
    @Mutation
    public SET_PREDICT_PERSONAL_CHUNKS_BEFORE(newData: KnowledgeChunks | null) {
        this.predictPersonalChunksBefore = newData;
    }
    @Mutation
    public SET_PREDICT_PERSONAL_CHUNKS_AFTER(newData: KnowledgeChunks | null) {
        this.predictPersonalChunksAfter = newData;
    }
    @Mutation
    public SET_PREDICT_DATA_STATISTICS(newData: PredictColumn[]) {
        this.predictDataStatistics = newData;
    }
    @Mutation
    public SET_PREDICT_RESULTS(newData: PredictResults | null) {
        this.predictResults = newData;
    }
    @Mutation
    public SET_ROUTE_TRIALS(route: string) {
        this.currentRouterViewTrials = route;
    }
    @Mutation
    public SET_ROUTE_KC_LIST(route: string) {
        this.currentRouterViewKCList = route;
    }
    @Mutation
    public SET_ROUTE_TRAIN_DATA(route: string) {
        this.currentRouterViewTrainData = route;
    }
    @Mutation
    public SET_ROUTE_TRAIN_RESULT(route: string) {
        this.currentRouterViewTrainResult = route;
    }
    @Mutation
    public SET_ROUTE_TRAIN_RESULT_ALL(route: string) {
        this.currentRouterViewTrainResultAll = route;
    }
    @Mutation
    public SET_ROUTE_PREDICT_DATA(route: string) {
        this.currentRouterViewPredictData = route;
    }
    @Mutation
    public SET_ROUTE_PREDICT_RESULT(route: string) {
        this.currentRouterViewPredictResult = route;
    }
    @Mutation
    public SET_CONFIRM_DIALOG_DATA(confirm: ConfirmDialogData | null) {
        this.confirmDialogData = confirm;
    }
    @Mutation
    public SET_NOTIFICATION_DIALOG_DATA(notification: NotificationDialogData | null) {
        this.notificationDialogData = notification;
    }
    @Mutation
    public SET_ERROR_DIALOG_DATA(err: ErrorDialogData | null) {
        this.errorDialogData = err;
    }

    /*----------------------------------*
     * getters
     *-----------------------------------*/
    public get getUuid(): string {
        return this.uuid;
    }
    public get getAgreed(): boolean {
        return this.agreed;
    }
    public get getAuthToken(): string {
        return this.authToken;
    }
    public get getListed(): number {
        return this.listed;
    }
    public get getKCListData(): KnowledgeChunks | null {
        return this.kclistData;
    }
    public get getTrained(): number {
        return this.trained;
    }
    public get getTrainingDataUploaded(): number {
        return this.trainingDataUploaded;
    }
    public get getTrainingData(): TrainData | null {
        return this.trainingData;
    }
    public get getInputTrainingDataHeaders(): any[] {
        return this.inputTrainingDataHeaders;
    }
    public get getPreprocessedTrainingData(): any[] {
        return this.preprocessedTrainingData;
    }
    public get getTrainKnowledgeChunks(): KnowledgeChunks | null {
        return this.trainKnowledgeChunks;
    }
    public get getTrainPersonalIndexBefore(): number {
        return this.trainPersonalIndexBefore;
    }
    public get getTrainPersonalIndexAfter(): number {
        return this.trainPersonalIndexAfter;
    }
    public get getTrainPersonalChunksBefore(): KnowledgeChunks | null {
        return this.trainPersonalChunksBefore;
    }
    public get getTrainPersonalChunksAfter(): KnowledgeChunks | null {
        return this.trainPersonalChunksAfter;
    }
    public get getTrainDataStatistics(): TrainColumn[] {
        return this.trainDataStatistics;
    }
    public get getTrainParams(): TrainParams | null {
        return this.trainParams;
    }
    public get getEnabledPredictPhase(): boolean {
        return this.enabledPredictPhase;
    }
    public get getPredicted(): number {
        return this.predicted;
    }
    public get getPredictDataUploaded(): number {
        return this.predictDataUploaded;
    }
    public get getPredictData(): PredictData | null {
        return this.predictData;
    }
    public get getInputPredictDataHeaders(): any[] {
        return this.inputPredictDataHeaders;
    }
    public get getPreprocessedPredictData(): PredictData | null {
        return this.preprocessedPredictData;
    }
    public get getPredictPersonalIndexBefore(): number {
        return this.predictPersonalIndexBefore;
    }
    public get getPredictPersonalIndexAfter(): number {
        return this.predictPersonalIndexAfter;
    }
    public get getPredictPersonalChunksBefore(): KnowledgeChunks | null {
        return this.predictPersonalChunksBefore;
    }
    public get getPredictPersonalChunksAfter(): KnowledgeChunks | null {
        return this.predictPersonalChunksAfter;
    }
    public get getPredictDataStatistics(): PredictColumn[] {
        return this.predictDataStatistics;
    }
    public get getPredictResults(): PredictResults | null {
        return this.predictResults;
    }
    public get getCurrentRouterViewTrials(): string {
        return this.currentRouterViewTrials;
    }
    public get getCurrentRouterViewKCList(): string {
        return this.currentRouterViewKCList;
    }
    public get getCurrentRouterViewTrainData(): string {
        return this.currentRouterViewTrainData;
    }
    public get getCurrentRouterViewTrainResult(): string {
        return this.currentRouterViewTrainResult;
    }
    public get getCurrentRouterViewTrainResultAll(): string {
        return this.currentRouterViewTrainResultAll;
    }
    public get getCurrentRouterViewPredictData(): string {
        return this.currentRouterViewPredictData;
    }
    public get getCurrentRouterViewPredictResult(): string {
        return this.currentRouterViewPredictResult;
    }
    public get getConfirmDialogData(): ConfirmDialogData | null {
        return this.confirmDialogData;
    }
    public get getNotificationDialogData(): NotificationDialogData | null {
        return this.notificationDialogData;
    }
    public get getErrorDialogData(): ErrorDialogData | null {
        return this.errorDialogData;
    }

    /*----------------------------------*
     * Actions
     *
     * ※値更新はmutationからのみ実施可能
     * ※mutationはActionからのみCall可能
     *-----------------------------------*/
    // セッション開始時の初期化
    @Action({})
    public init() {
        this.SET_UUID('');
        this.SET_AGREED(false);
        this.SET_AUTHTOKEN('');
        this.format();
    }

    // 初期化ボタン用 アクション
    @Action({})
    public format() {
        // 予測データの初期化
        this.formatPredict();

        // KC列挙
        this.SET_LISTED(SC.Flag.Process.TODO);
        this.SET_KCLIST_DATA(null);

        // 学習
        this.SET_TRAINED(SC.Flag.Process.TODO);
        this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.TODO);
        this.SET_TRAINING_DATA(null);
        this.SET_INPUT_TRAINING_DATA_HEADERS([]);
        this.SET_PREPROCESSED_TRAINING_DATA([]);
        this.SET_TRAIN_CHUNKS(null);
        this.SET_TRAIN_PERSONAL_INDEX_BEFORE(0);
        this.SET_TRAIN_PERSONAL_INDEX_AFTER(0);
        this.SET_TRAIN_PERSONAL_CHUNKS_BEFORE(null);
        this.SET_TRAIN_PERSONAL_CHUNKS_AFTER(null);
        this.SET_TRAIN_PARAMS(null);
        this.SET_TRAIN_DATA_STATISTICS([]);

        // 表示コンポーネント
        this.SET_ROUTE_TRIALS(SC.Path.TRAIN_DATA_MENU);
        this.SET_ROUTE_TRAIN_DATA(SC.Path.TRAIN_DATA_UPLOAD);
        this.SET_ROUTE_KC_LIST(SC.Path.TRAIN_KC_LIST);
        this.SET_ROUTE_TRAIN_RESULT(SC.Path.TRAIN_RESULT_ALL);
        this.SET_ROUTE_TRAIN_RESULT_ALL(SC.Path.TRAIN_RESULT_ALL_KC);

        // ダイアログ
        this.SET_CONFIRM_DIALOG_DATA(null);
        this.SET_NOTIFICATION_DIALOG_DATA(null);
        this.SET_ERROR_DIALOG_DATA(null);

        // 予測
        this.SET_ENABLED_PREDICT_PHASE(false);
    }

    // 予測データの初期化ボタン用 アクション
    @Action
    public formatPredict() {
        // 予測
        this.SET_PREDICTED(SC.Flag.Process.TODO);
        this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.TODO);
        this.SET_PREDICT_DATA(null);
        this.SET_INPUT_PREDICT_DATA_HEADERS([]);
        this.SET_PREPROCESSED_PREDICT_DATA(null);
        this.SET_PREDICT_PERSONAL_INDEX_BEFORE(0);
        this.SET_PREDICT_PERSONAL_INDEX_AFTER(0);
        this.SET_PREDICT_PERSONAL_CHUNKS_BEFORE(null);
        this.SET_PREDICT_PERSONAL_CHUNKS_AFTER(null);
        this.SET_PREDICT_DATA_STATISTICS([]);
        this.SET_PREDICT_RESULTS(null);

        // 表示コンポーネント
        this.SET_ROUTE_TRIALS(SC.Path.PREDICT_DATA_MENU);
        this.SET_ROUTE_PREDICT_DATA(SC.Path.PREDICT_DATA_UPLOAD);
        this.SET_ROUTE_PREDICT_RESULT(SC.Path.PREDICT_RESULT_DATA);
    }

    // プライバシーポリシー同意時の更新処理
    @Action({})
    public agreeWithPrivacyPolicy() {
        this.SET_AGREED(true);
    }

    // 確認ダイアログを表示
    @Action({})
    public showConfirmDialog(confirm: ConfirmDialogData): void {
        this.SET_CONFIRM_DIALOG_DATA(confirm);
    }

    // 確認ダイアログを閉じる
    @Action({})
    public closeConfirmDialog(): void {
        this.SET_CONFIRM_DIALOG_DATA(null);
    }

    // 通知ダイアログを表示
    @Action({})
    public showNotificationDialog(notification: NotificationDialogData): void {
        this.SET_NOTIFICATION_DIALOG_DATA(notification);
    }

    // 通知ダイアログを閉じる
    @Action({})
    public closeNotificationDialog(): void {
        this.SET_NOTIFICATION_DIALOG_DATA(null);
    }

    // エラーダイアログを表示
    @Action({})
    public showErrorDialog(err: ErrorDialogData): void {
        if (!err.title || !err.messageKey) {
            err = new ErrorDialogData();
        }
        this.SET_ERROR_DIALOG_DATA(err);
    }

    // エラーダイアログを閉じる
    @Action({})
    public closeErrorDialog(): void {
        // ダイアログ時の処理が必要であれば実施
        if (this.getErrorDialogData) {
            // リロードフラグが立っていればリロード
            if (this.getErrorDialogData.needCloseAndReload) {
                location.href = SC.Path.APP_BASE;
                return;
            }

            // トークンのクリア
            if (this.getErrorDialogData.needClearToken) {
                this.SET_AUTHTOKEN('');
            }

            // 初期化処理実行
            if (this.getErrorDialogData.needInit) {
                this.format();
            }
        }

        this.SET_ERROR_DIALOG_DATA(null);
    }

    // API呼び出し用tokenの取得
    @Action({})
    public async getToken(): Promise<boolean> {
        try {
            const token = await Api.getToken();
            this.SET_AUTHTOKEN(token);
            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 学習データ登録
    @Action({})
    public async registTrainingSet(csv: File): Promise<boolean> {
        try {
            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.DOING);
            await Api.registTrainingSet(csv);
            const trainingSet = await Api.getTrainingSet();
            const trainData = new TrainData(trainingSet, this.getInputTrainingDataHeaders);
            this.SET_TRAINING_DATA(trainData);

            // 集計処理
            const trainDataStatistics = new TrainDataStatistics(trainData);
            this.SET_TRAIN_DATA_STATISTICS(trainDataStatistics.statistics);

            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.DONE);
            return true;
        } catch (err) {
            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.TODO);
            this.showErrorDialog(err);
            return false;
        }
    }

    // 学習データ指定
    @Action({})
    public async selectTrainingSet({caseId, fileId, language = 'ja'}: {caseId: string, fileId: string, language: string}): Promise<boolean> {
        try {
            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.DOING);
            await Api.selectTrainingSet(fileId, language);
            const trainingSet = await Api.getTrainingSet();
            const trainData = new TrainData(trainingSet, this.getInputTrainingDataHeaders, caseId, fileId, language);
            this.SET_TRAINING_DATA(trainData);

            // 集計処理
            const trainDataStatistics = new TrainDataStatistics(trainData);
            this.SET_TRAIN_DATA_STATISTICS(trainDataStatistics.statistics);

            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.DONE);
            return true;
        } catch (err) {
            this.SET_TRAINING_DATA_UPLOADED(SC.Flag.Process.TODO);
            this.showErrorDialog(err);
            return false;
        }
    }

    // 学習開始
    @Action
    public async startCreateModel(): Promise<boolean> {
        try {
            await Api.startCreateModel();
            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 学習状況の取得
    @Action
    public async getModelCreationState(): Promise<string | undefined> {
        try {
            const status = await Api.getModelCreationState();
            return status;
        } catch (err) {
            this.showErrorDialog(err);
            return err.errResponse.status;
        }
    }

    // ナレッジチャンク一覧取得
    @Action
    public async getKcListForModel(): Promise<boolean> {
        try {
            const response = await Api.getKcListForModel();
            const kcList = new KnowledgeChunks(response);
            this.SET_KCLIST_DATA(kcList);

            return true;
        } catch (err) {
            this.showErrorDialog(err);

            return false;
        }
    }

    // 学習処理
    @Action
    public async training(): Promise<boolean> {
        try {
            // パラメータとスコアの取得
            const params = await Api.getParams();
            const paramsAndScores = new TrainParams(params);

            this.SET_TRAIN_PARAMS(paramsAndScores);

            // 前処理済みデータは初期リリース時は表示しないのでコメントアウト
            // 作成が完了したWLモデルの前処理済みデータ取得
            // const preprocessed = await Api.getPreprocessedTrainingSet();
            // this.SET_PREPROCESSED_TRAINING_DATA(preprocessed);

            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 学習データに対応するナレッジチャンクの取得
    @Action
    public async getPersonalChunksForTrainingSet({
        index,
        isPreprocessed,
    }: {
        [key: string]: any;
    }): Promise<boolean> {
        try {
            // indexに対応するナレッジチャンクの取得
            const response = await Api.getPersonalChunksForTraingSet(index);
            if (response.length <= 1) {
                throw new ErrorDialogData('error.message.nodata');
            }
            const kcList = new KnowledgeChunks(response);

            // 対象がバイナリ化前と後かで保存先変更
            if (isPreprocessed) {
                this.SET_TRAIN_PERSONAL_INDEX_AFTER(index + 1);
                this.SET_TRAIN_PERSONAL_CHUNKS_AFTER(kcList);
            } else {
                this.SET_TRAIN_PERSONAL_INDEX_BEFORE(index + 1);
                this.SET_TRAIN_PERSONAL_CHUNKS_BEFORE(kcList);
            }
            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 予測データ登録
    @Action({})
    public async registPredictSet(csv: File): Promise<boolean> {
        try {
            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.DOING);
            await Api.registPredictSet(csv);
            const predictSet = await Api.getPredictSet();
            const predictData = new PredictData(predictSet, this.getInputPredictDataHeaders);
            this.SET_PREDICT_DATA(predictData);

            // 集計処理
            const predictDataStatistics = new PredictDataStatistics(predictData);
            this.SET_PREDICT_DATA_STATISTICS(predictDataStatistics.statistics);

            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.DONE);
            return true;
        } catch (err) {
            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.TODO);
            this.showErrorDialog(err);

            return false;
        }
    }

    // 予測データ指定
    @Action({})
    public async selectPredictSet({caseId, fileId, language = 'ja'}: {caseId: string, fileId: string, language: string}): Promise<boolean> {
        try {
            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.DOING);
            await Api.selectPredictSet(fileId, language);
            const predictSet = await Api.getPredictSet();
            const predictData = new PredictData(predictSet, this.getInputPredictDataHeaders);
            this.SET_PREDICT_DATA(predictData);

            // 集計処理
            const predictDataStatistics = new PredictDataStatistics(predictData);
            this.SET_PREDICT_DATA_STATISTICS(predictDataStatistics.statistics);

            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.DONE);
            return true;
        } catch (err) {
            this.SET_PREDICT_DATA_UPLOADED(SC.Flag.Process.TODO);
            this.showErrorDialog(err);

            return false;
        }
    }

    // 予測開始
    @Action
    public async startPredict(): Promise<boolean> {
        try {
            await Api.startPredict();
            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 予測状況の取得
    @Action
    public async getPredictState(): Promise<string | undefined> {
        try {
            const status = await Api.getPredictState();
            return status;
        } catch (err) {
            this.showErrorDialog(err);
            return err.errResponse.status;
        }
    }

    // 予測結果の取得
    @Action
    public async getPredictResult(): Promise<boolean> {
        try {
            // 結果の取得
            const responseResults = await Api.getPredictResult();
            const results = new PredictResults(responseResults);
            this.SET_PREDICT_RESULTS(results);

            // 前処理済みデータは初期リリース時は表示しないのでコメントアウト
            // 作成が完了したWLモデルの前処理済みデータ取得
            // const responsePreprocessed = await Api.getPreprocessedPredictSet();
            // const preprocessed = new PredictData(responsePreprocessed);

            // 結果を反映したデータ作成
            // オリジナルデータ
            const original = this.getPredictData;
            original && original.createWithResult(results);
            this.SET_PREDICT_DATA(original);

            // 前処理済みデータは初期リリース時は表示しないのでコメントアウト
            // 前処理済みデータ
            // preprocessed.createWithResult(results);
            // this.SET_PREPROCESSED_PREDICT_DATA(preprocessed);

            return true;
        } catch (err) {
            this.showErrorDialog(err);
            return false;
        }
    }

    // 予測データに対応するナレッジチャンクの取得
    @Action
    public async getPersonalChunksForPredictSet({
        index,
        isPreprocessed,
    }: {
        [key: string]: any;
    }): Promise<boolean> {
        try {
            // indexに対応するナレッジチャンクの取得
            const response = await Api.getPersonalChunksForPredictSet(index);
            if (response.length == 0) {
                throw new ErrorDialogData('error.message.nodata');
            }

            const kcList = new KnowledgeChunks(response);

            // 対象がバイナリ化前と後かで保存先変更
            if (isPreprocessed) {
                this.SET_PREDICT_PERSONAL_INDEX_AFTER(index + 1);
                this.SET_PREDICT_PERSONAL_CHUNKS_AFTER(kcList);
            } else {
                this.SET_PREDICT_PERSONAL_INDEX_BEFORE(index + 1);
                this.SET_PREDICT_PERSONAL_CHUNKS_BEFORE(kcList);
            }

            return true;
        } catch (err) {
            this.showErrorDialog(err);

            return false;
        }
    }
}

export const sessionModule = getModule(Session);
